/**
 * PlanBuilderScreen — pantalla del editor.
 *
 * Layout 3 columnas:
 *   ┌────────────┬────────────────────┬────────────┐
 *   │ Sidebar    │  Preview A4        │ Meta panel │
 *   │ secciones  │  (sections list)   │ (cliente,  │
 *   │ (240px)    │  scrollable        │  share,    │
 *   │            │  fluido            │  save…)    │
 *   └────────────┴────────────────────┴────────────┘
 *
 * Drag-and-drop nativo HTML5 para reordenar secciones (no librería).
 * Autosave 1500ms via usePlanAutosave. Hash format:
 *   #/plan-builder?id=42       → carga existente
 *   #/plan-builder?new=cover   → crea nuevo y arranca enfocado en cover
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../lib/toast";
import { Icon } from "../lib/Icon";
import {
  createPlan,
  createShare,
  getPlan,
  publicShareUrl,
  updatePlan as apiUpdatePlan,
  type Plan,
} from "../api/plans";
import {
  getClient,
  listClients,
  type ClientFull,
} from "../api/clients";
import { listMeasurements } from "../api/measurements";
import { absUploadUrl } from "../api/client";
import { exportPlanPdf } from "../lib/planPdf";
import { ClientFormModal } from "./clients/ClientFormModal";
import {
  SECTION_DESC,
  SECTION_ICONS,
  SECTION_LABELS,
  SECTION_REQUIRED,
  applyBrandCoverDefaults,
  availableSectionTypes,
  brandFromUser,
  defaultPlan,
  defaultSection,
  type PlanDoc,
  type PlanSection,
  type RoutineWeek,
  type SectionType,
  type StylePreset,
} from "../types/plan";
import { getTemplate } from "../types/templates";
import { SectionView } from "./plan/sections/SectionView";
import { PlanCalendar } from "./plan/PlanCalendar";
import { prefillSectionsFromClient, prefillMeasurementsSection, seedDietCalculatorFromClient } from "./plan/prefillFromClient";
import { PrintablePlanView } from "./plan/PrintablePlanView";
import { SwapTemplateModal } from "./plan/SwapTemplateModal";
import { AiGeneratePlanModal } from "./plan/AiGeneratePlanModal";
import { aiPlanDocToSections } from "../lib/aiPlanDocMapper";
import { fontStack, ensurePlanFontsLoaded } from "../lib/fontStack";
import { usePlanAutosave } from "./plan/usePlanAutosave";
import { EditorTopbarTools } from "./plan/EditorTopbarTools";
import { BrandApplyButton } from "./plan/BrandApplyButton";
import { TextStylePopover } from "./plan/TextStylePopover";
import { EditorStyleProvider, type EditorStyleValue } from "./plan/EditorStyleContext";
import type { FieldStyle } from "../types/plan";

interface PlanBuilderScreenProps {
  /** Callback para volver a la pantalla anterior (dashboard / templates). */
  onBack?: () => void;
}

interface HashParams {
  id: number | null;
  newType: SectionType | null;
  clientId: number | null;
}

function parseHashParams(): HashParams {
  const raw = (typeof window !== "undefined" ? window.location.hash : "").replace(/^#\/?/, "");
  const qsIdx = raw.indexOf("?");
  if (qsIdx < 0) return { id: null, newType: null, clientId: null };
  const params = new URLSearchParams(raw.slice(qsIdx + 1));
  const idStr = params.get("id");
  const newType = params.get("new") as SectionType | null;
  const clientStr = params.get("client");
  return {
    id: idStr ? Number(idStr) : null,
    newType,
    clientId: clientStr ? Number(clientStr) : null,
  };
}

export function PlanBuilderScreen({ onBack }: PlanBuilderScreenProps) {
  const { user, updateProfile } = useAuth();
  const toast = useToast();
  const [plan, setPlan] = useState<PlanDoc | null>(null);
  const [dbId, setDbId] = useState<number | null>(null);
  /** Cliente vinculado al plan (FK). Si null, plan es "huérfano". */
  const [linkedClientId, setLinkedClientId] = useState<number | null>(null);
  /** Nombre denormalizado del cliente vinculado (para UX del chip). */
  const [linkedClientName, setLinkedClientName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  // Popover del selector de cliente (en topbar)
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  // Panel lateral derecho (cliente / share / export). Oculto por defecto:
  // se abre con el botón "Detalles" de la topbar o automáticamente al disparar
  // un share (`handleShare` lo abre para mostrar la URL recién generada).
  const [showDrawer, setShowDrawer] = useState(false);
  // Modal "Cambiar plantilla" — swap visual sin perder contenido. Ver
  // SwapTemplateModal + applyTemplateStyleOnly en templates.ts.
  const [swapTplOpen, setSwapTplOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  // Modal "Generar con IA" — abre el wizard para regenerar las secciones
  // del plan actual. Conserva brand/template/coverStyle del plan vigente.
  // Cuando viene "training" o "nutrition" desde los botones inline de las
  // secciones, pre-tilda solo el toggle correspondiente; "all" tilda ambos.
  const [aiGenOpen, setAiGenOpen] = useState<false | "training" | "nutrition" | "all">(false);
  // Sección actualmente visible en el viewport — usado por el picker de
  // íconos para insertar la decoración EN LA SECCIÓN QUE VES, no en una
  // que tengas que elegir de una lista. Trackeado vía IntersectionObserver
  // sobre `[id="pg-${sectionId}"]`. Fallback al primer enabled si nada
  // está en viewport todavía (ej. modal abierto antes de scrollear).
  const [visibleSectionId, setVisibleSectionId] = useState<string | null>(null);
  // For drag/reorder
  const dragSrcId = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  // ---------- Cargar / crear plan según hash ----------
  useEffect(() => {
    const { id, newType, clientId } = parseHashParams();
    let cancelled = false;

    const init = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        if (id) {
          const remote = await getPlan(id);
          if (cancelled) return;
          const doc = adoptRemotePlan(remote);
          // Si el plan no tiene brand, hidratamos con el del user (no se guarda hasta editar).
          if (!doc.brand && user) doc.brand = brandFromUser(user);
          // Si el template del plan está en el catálogo y faltan primary/accent,
          // los completamos con la paleta del template (no pisa colores del coach).
          applyTemplatePaletteIfMissing(doc);
          setPlan(doc);
          setDbId(remote.id);
          setLinkedClientId(remote.client_id ?? null);
          setLinkedClientName(remote.client_name || "");
        } else {
          // Plan nuevo. Si vino ?client=N, lo creamos vinculado a ese cliente
          // (el backend sincroniza client_name automáticamente al recibir client_id).
          const fresh = defaultPlan({
            template: "t-bold",
            clientName: "Cliente",
            brand: brandFromUser(user),
          });
          // Feature 2: sembrar los textos de portada con los defaults de la marca.
          applyBrandCoverDefaults(fresh, user);
          // Si vino ?new=cover, lo dejamos sólo con esa sección activa
          if (newType) {
            fresh.sections = fresh.sections.map((s) => ({ ...s, enabled: s.type === newType }));
          }
          const remote = await createPlan({
            name: fresh.name,
            template: fresh.template,
            client_id: clientId ?? null,
            client_name: clientId ? null : "Cliente",
            sections: fresh.sections,
            brand: fresh.brand,
          });
          if (cancelled) return;
          // El backend sincroniza client_name si vino client_id; si vino, hidratamos las secciones
          if (remote.client_name) {
            applyClientNameToSections(fresh, remote.client_name);
          }
          // Si el plan se creó vinculado a un cliente (flujo "Crear plan desde
          // cliente"), precargamos sus datos en las secciones `cover` y `client`
          // usando el perfil + la última medición registrada.
          if (clientId) {
            try {
              const [client, measurements] = await Promise.all([
                getClient(clientId),
                listMeasurements(clientId),
              ]);
              if (cancelled) return;
              prefillSectionsFromClient(fresh, client, measurements);
              // Poblar la sección "measurements" con el histórico del cliente
              // (hasta las últimas 4 tomas) — y caer al record del cliente para
              // los perímetros que la ficha tiene directos (cintura, cadera…).
              prefillMeasurementsSection(fresh, client, measurements);
            } catch {
              // Precarga best-effort: si falla, el plan queda vinculado igual
              // y el coach puede completar los datos manualmente.
            }
          }
          setPlan({ ...fresh });
          setDbId(remote.id);
          setLinkedClientId(remote.client_id ?? null);
          setLinkedClientName(remote.client_name || "");
          // Si precargamos secciones desde el cliente, persistimos para que el
          // refresh (que recarga por ?id=) traiga los datos ya hidratados.
          if (clientId) {
            try {
              await apiUpdatePlan(remote.id, { sections: fresh.sections });
            } catch {
              // Si falla el persist, las secciones siguen en memoria y el
              // autosave las guardará en cuanto el coach edite algo.
            }
          }
          // Reemplazar hash para que un refresh recargue el mismo plan
          const newHash = `#/plan-builder?id=${remote.id}`;
          window.history.replaceState(null, "", newHash);
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
    // user.id es estable por sesión; intencionalmente no incluimos `user` en deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Autosave ----------
  const { saving, lastSavedAt, save } = usePlanAutosave(
    plan,
    dirty,
    dbId,
    () => setDirty(false),
  );

  // Tema del plan: "dark" lo setean las plantillas dark-editorial vía applyDefaults.
  const isDarkPlan = plan?.brand?.theme === "dark";

  // Densidad del plan (Feature 3): clase que escala el padding de las páginas.
  const densityClass =
    plan?.brand?.density === "compact"
      ? "al-density-compact"
      : plan?.brand?.density === "spacious"
      ? "al-density-spacious"
      : "";

  // Garantizar que las fuentes extra (Playfair/Montserrat/Oswald) estén cargadas.
  useEffect(() => {
    ensurePlanFontsLoaded();
  }, []);

  // IntersectionObserver: trackea qué sección está más visible en el viewport
  // para que el picker de íconos pueda insertar EN LA HOJA QUE ESTÁS VIENDO
  // (no requiere elegir destino en un dropdown). Re-armado cuando cambian las
  // secciones activas (agregar/quitar/reordenar).
  const sectionIds = useMemo(
    () => (plan?.sections || []).filter((s) => s.enabled !== false).map((s) => s.id).join(","),
    [plan?.sections],
  );
  useEffect(() => {
    if (!sectionIds) return;
    const ids = sectionIds.split(",").filter(Boolean);
    if (ids.length === 0) return;
    // Estado local: mejor ratio + id de cada sección. Usamos refs para no
    // re-renderizar en cada intersection event (solo cuando cambia el ganador).
    const ratios = new Map<string, number>();
    let winner: string | null = null;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const id = (e.target as HTMLElement).id.replace(/^pg-/, "");
          ratios.set(id, e.intersectionRatio);
        }
        // Sección con mayor intersection ratio = la que el coach está mirando
        let best: string | null = null;
        let bestRatio = 0;
        ratios.forEach((r, id) => {
          if (r > bestRatio) { best = id; bestRatio = r; }
        });
        if (best && best !== winner) {
          winner = best;
          setVisibleSectionId(best);
        }
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    const els: HTMLElement[] = [];
    for (const id of ids) {
      const el = document.getElementById(`pg-${id}`);
      if (el) { obs.observe(el); els.push(el); }
    }
    // Fallback inmediato: si nada está visible aún, usamos el primero.
    if (!winner) setVisibleSectionId(ids[0]);
    return () => { obs.disconnect(); ratios.clear(); };
  }, [sectionIds]);

  // Inyectar branding como CSS vars en el contenedor del preview
  const previewStyle = useMemo<CSSProperties>(() => {
    const accent = plan?.brand?.accent || user?.brand_color_accent || "#ec4899";
    const primary = plan?.brand?.primary || user?.brand_color_primary || "#7c3aed";
    const style: CSSProperties = {
      ["--al-accent" as any]: accent,
      ["--al-primary" as any]: primary,
      // En tema dark el fondo de las páginas se invierte (lo refuerza .al-plan-dark
      // en CSS, pero seteamos las vars también para que las herede el A4).
      ["--al-bg" as any]: isDarkPlan ? "#0a0a0a" : "#ffffff",
      ["--al-ink" as any]: isDarkPlan ? "#f4f4f5" : "#0a0a0a",
    };
    // Tipografía del preset (Feature 3): solo override si el plan la define,
    // así los planes sin brand mantienen el default global (Bricolage/Inter).
    if (plan?.brand?.displayFont) {
      (style as any)["--font-display"] = fontStack(plan.brand.displayFont);
    }
    if (plan?.brand?.bodyFont) {
      (style as any)["--font-body"] = fontStack(plan.brand.bodyFont);
    }
    return style;
  }, [plan, user, isDarkPlan]);

  // ---------- Mutators ----------
  const updatePlan = useCallback((next: PlanDoc) => {
    setPlan(next);
    setDirty(true);
  }, []);

  // ---------- EditorStyleContext value (estilos por campo persistidos en JSONB) ----------
  const editorStyleValue = useMemo<EditorStyleValue>(() => ({
    readonly: false,
    getFieldStyle: (sectionId, fieldPath) => {
      const sec = plan?.sections.find((s) => s.id === sectionId);
      return sec?.styles?.[fieldPath];
    },
    setFieldStyle: (sectionId, fieldPath, style) => {
      if (!plan) return;
      const sections = plan.sections.map((s) => {
        if (s.id !== sectionId) return s;
        const styles = { ...(s.styles || {}) };
        if (!style || Object.keys(style).length === 0) {
          delete styles[fieldPath];
        } else {
          styles[fieldPath] = style;
        }
        return { ...s, styles } as PlanSection;
      });
      updatePlan({ ...plan, sections, updatedAt: Date.now() });
    },
    patchFieldStyle: (sectionId, fieldPath, patch) => {
      if (!plan) return;
      const sections = plan.sections.map((s) => {
        if (s.id !== sectionId) return s;
        const cur = s.styles?.[fieldPath] || {};
        const merged: FieldStyle = { ...cur, ...patch };
        // Limpiar keys con valor undefined o vacío para mantener el objeto compacto
        for (const k of Object.keys(merged) as (keyof FieldStyle)[]) {
          if (merged[k] === undefined || merged[k] === "") delete merged[k];
        }
        const styles = { ...(s.styles || {}) };
        if (Object.keys(merged).length === 0) {
          delete styles[fieldPath];
        } else {
          styles[fieldPath] = merged;
        }
        return { ...s, styles } as PlanSection;
      });
      updatePlan({ ...plan, sections, updatedAt: Date.now() });
    },
    clearFieldStyle: (sectionId, fieldPath) => {
      if (!plan) return;
      const sections = plan.sections.map((s) => {
        if (s.id !== sectionId) return s;
        if (!s.styles?.[fieldPath]) return s;
        const styles = { ...s.styles };
        delete styles[fieldPath];
        return { ...s, styles } as PlanSection;
      });
      updatePlan({ ...plan, sections, updatedAt: Date.now() });
    },
  }), [plan, updatePlan]);

  const updateSection = useCallback(
    (sectionId: string, next: PlanSection) => {
      if (!plan) return;
      const sections = plan.sections.map((s) => (s.id === sectionId ? next : s));
      updatePlan({ ...plan, sections, updatedAt: Date.now() });
    },
    [plan, updatePlan],
  );

  const toggleSection = useCallback(
    (sectionId: string) => {
      if (!plan) return;
      const sections = plan.sections.map((s) =>
        s.id === sectionId ? ({ ...s, enabled: !s.enabled } as PlanSection) : s,
      );
      updatePlan({ ...plan, sections });
    },
    [plan, updatePlan],
  );

  const removeSection = useCallback(
    (sectionId: string) => {
      if (!plan) return;
      const s = plan.sections.find((x) => x.id === sectionId);
      if (!s) return;
      if (SECTION_REQUIRED[s.type]) {
        toast("Esta sección no se puede eliminar (sólo desactivar).");
        return;
      }
      if (!confirm(`¿Eliminar la sección "${SECTION_LABELS[s.type]}"?`)) return;
      const sections = plan.sections.filter((x) => x.id !== sectionId);
      updatePlan({ ...plan, sections });
    },
    [plan, updatePlan, toast],
  );

  const addSection = useCallback(
    (type: SectionType) => {
      if (!plan) return;
      if (plan.sections.find((s) => s.type === type)) return;
      const sec = defaultSection(type);
      updatePlan({ ...plan, sections: [...plan.sections, sec] });
      setShowAddMenu(false);
    },
    [plan, updatePlan],
  );

  const reorderSections = useCallback(
    (srcId: string, dstId: string) => {
      if (!plan || srcId === dstId) return;
      const sections = [...plan.sections];
      const srcIdx = sections.findIndex((s) => s.id === srcId);
      const dstIdx = sections.findIndex((s) => s.id === dstId);
      if (srcIdx < 0 || dstIdx < 0) return;
      const [removed] = sections.splice(srcIdx, 1);
      sections.splice(dstIdx, 0, removed);
      updatePlan({ ...plan, sections });
    },
    [plan, updatePlan],
  );

  const renamePlan = useCallback(
    (newName: string) => {
      if (!plan) return;
      updatePlan({ ...plan, name: newName });
    },
    [plan, updatePlan],
  );

  /**
   * Vincular un cliente al plan: persiste `client_id` en backend (que sincroniza
   * `client_name`) y actualiza el preview en vivo (cover.clientName +
   * client.fullName) para que el coach vea el cambio sin refresh.
   */
  const linkClient = useCallback(
    async (client: ClientFull) => {
      if (!dbId || !plan) return;
      try {
        await apiUpdatePlan(dbId, { client_id: client.id });
        setLinkedClientId(client.id);
        setLinkedClientName(client.full_name);
        // Sync visual del preview
        const next: PlanDoc = { ...plan, sections: plan.sections.map((s) => ({ ...s })) };
        applyClientNameToSections(next, client.full_name);
        setPlan(next);
        // No marcamos dirty: el backend ya tiene el client_name actualizado
        // y las secciones acaban de sincronizarse. Si el coach cambia algo
        // después, autosave persistirá las secciones también.
        toast(`Plan vinculado a ${client.full_name}`);
        setShowClientPicker(false);
      } catch (e) {
        toast("No pude vincular el cliente: " + (e instanceof Error ? e.message : ""));
      }
    },
    [dbId, plan, toast],
  );

  const unlinkClient = useCallback(async () => {
    if (!dbId) return;
    try {
      await apiUpdatePlan(dbId, { client_id: null });
      setLinkedClientId(null);
      setLinkedClientName("");
      toast("Cliente desvinculado");
      setShowClientPicker(false);
    } catch (e) {
      toast("No pude desvincular: " + (e instanceof Error ? e.message : ""));
    }
  }, [dbId, toast]);

  // Estado de impresión: cuando es true se monta <PrintablePlanView> (versión
  // limpia, solo-lectura) y, una vez cargadas sus imágenes, se dispara
  // window.print(). El navegador genera el PDF con cortes de página perfectos
  // (las reglas @media print evitan partir ejercicios/comidas a la mitad) y
  // texto seleccionable. La versión imprimible NO muestra controles del
  // entrenador (agregar ejercicio/alimento, etc.) — usa SectionView readonly.
  const [printing, setPrinting] = useState(false);

  const handlePrint = useCallback(async () => {
    if (!plan || printing) return;
    // Guardar primero para que la versión impresa refleje los últimos cambios.
    if (dirty) {
      try {
        await save();
      } catch {
        // Continuar — el preview en memoria es la fuente de verdad.
      }
    }
    setPrinting(true);
  }, [plan, printing, dirty, save]);

  // Cuando el print view se montó: esperar a que sus imágenes carguen y
  // disparar el diálogo de impresión del navegador.
  useEffect(() => {
    if (!printing) return;
    let cancelled = false;
    const run = async () => {
      // Dos frames para que React pinte el print view.
      await new Promise((r) =>
        requestAnimationFrame(() => requestAnimationFrame(() => r(null))),
      );
      if (cancelled) return;
      const root = document.querySelector(".al-print-root");
      if (root) {
        const imgs = Array.from(root.querySelectorAll("img"));
        const waitImgs = Promise.all(
          imgs.map((img) =>
            img.complete
              ? Promise.resolve()
              : new Promise((res) => {
                  img.addEventListener("load", () => res(null), { once: true });
                  img.addEventListener("error", () => res(null), { once: true });
                }),
          ),
        );
        // Tope de espera por si alguna imagen se cuelga.
        await Promise.race([waitImgs, new Promise((r) => setTimeout(r, 4000))]);
        await new Promise((r) => setTimeout(r, 150)); // respiro para fuentes/layout
      }
      if (cancelled) return;
      const el = root as HTMLElement | null;
      if (!el) {
        setPrinting(false);
        return;
      }
      try {
        const brand = plan?.brand;
        const wmUrl = brand?.watermark && brand?.logoUrl ? absUploadUrl(brand.logoUrl) : null;
        const safeName =
          (plan?.name || linkedClientName || "Plan AetherLite")
            .replace(/[\\/:*?"<>|]+/g, " ")
            .trim() || "Plan AetherLite";
        await exportPlanPdf({
          root: el,
          fileName: safeName,
          watermarkUrl: wmUrl,
          dark: brand?.theme === "dark",
        });
      } catch (e) {
        toast("No pude generar el PDF: " + (e instanceof Error ? e.message : String(e)));
      } finally {
        if (!cancelled) setPrinting(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [printing]);

  const handleShare = useCallback(async () => {
    if (!dbId) return;
    setSharing(true);
    try {
      // Forzar guardado primero para que el share refleje cambios pendientes
      if (dirty) await save();
      const share = await createShare(dbId);
      const url = publicShareUrl(share.short_code);
      setShareUrl(url);
      // Abrir el drawer automáticamente para mostrar la URL recién generada
      setShowDrawer(true);
      try {
        await navigator.clipboard.writeText(url);
        toast("Enlace copiado al portapapeles");
      } catch {
        toast("Enlace generado");
      }
    } catch (e) {
      toast("Error al generar enlace: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSharing(false);
    }
  }, [dbId, dirty, save, toast]);

  if (loading) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "60vh", color: "var(--fg-muted)" }}>
        Cargando plan…
      </div>
    );
  }

  if (loadError || !plan) {
    return (
      <div className="page" style={{ paddingTop: 48 }}>
        <div className="card" style={{ padding: 32, textAlign: "center" }}>
          <h3>No se pudo cargar el plan</h3>
          <p className="muted">{loadError || "Plan no encontrado"}</p>
          <button className="btn btn-secondary" onClick={onBack}>Volver</button>
        </div>
      </div>
    );
  }

  const available = availableSectionTypes(plan);
  const activeSections = plan.sections.filter((s) => s.enabled);

  return (
    <EditorStyleProvider value={editorStyleValue}>
    <div className="al-pb">
      {/* TopBar específica del editor */}
      <div className="al-pb-topbar">
        <button className="btn btn-ghost btn-sm" onClick={onBack} title="Volver">
          <Icon name="arrowL" size={14} /> Volver
        </button>
        <input
          className="al-pb-title-input"
          value={plan.name}
          onChange={(e) => renamePlan(e.target.value)}
          placeholder="Nombre del plan"
        />
        <ClientChip
          name={linkedClientName}
          onClick={() => setShowClientPicker(true)}
        />
        <div className="al-pb-status">
          {saving ? (
            <span className="muted tiny">Guardando…</span>
          ) : lastSavedAt ? (
            <span className="muted tiny">
              Guardado · {formatTime(lastSavedAt)}
            </span>
          ) : dirty ? (
            <span className="muted tiny">Sin guardar</span>
          ) : (
            <span className="muted tiny">Listo</span>
          )}
        </div>
        {/* Tools del editor: editor de estilo (preset) + íconos draggables */}
        <EditorTopbarTools
          brand={plan.brand}
          onBrandChange={(patch) => {
            updatePlan({
              ...plan,
              brand: { ...(plan.brand || {}), ...patch },
            });
          }}
          presets={user?.style_presets ?? []}
          onPresetsChange={async (presets) => {
            try {
              await updateProfile({ style_presets: presets });
            } catch {
              toast("No pude guardar los presets de estilo");
            }
          }}
          toast={toast}
          activeSectionId={visibleSectionId}
          onInsertDecoration={(iconId) => {
            // El picker NO pide destino — se inserta en la sección que el
            // coach está mirando ahora (trackeada via IntersectionObserver).
            // Fallback al primer enabled si nada está visible todavía.
            const targetId =
              visibleSectionId ||
              plan.sections.find((s) => s.enabled !== false)?.id;
            if (!targetId) return;
            const newDec = {
              id: `dec_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
              icon: iconId,
              x: 50,
              y: 50,
              size: 72,
              rotation: 0,
              opacity: 1,
            };
            updatePlan({
              ...plan,
              sections: plan.sections.map((s) =>
                s.id === targetId
                  ? { ...s, decorations: [...(s.decorations || []), newDec] }
                  : s,
              ),
            });
          }}
        />
        {/* Aplicar la marca del coach (logo, datos, colores, portada, foto) al plan. */}
        <BrandApplyButton
          user={user}
          plan={plan}
          toast={toast}
          onApply={(patch) =>
            updatePlan({ ...plan, brand: { ...(plan.brand || {}), ...patch } })
          }
          onApplyToPlan={(mutator) => updatePlan(mutator(plan))}
        />
        {/* Cambiar plantilla VISUAL del plan — conserva todo el contenido y
            solo cambia portada/paleta/tipografía/theme. Útil para reutilizar
            un plan en otro contexto (ej. mismo plan ahora con look masculino). */}
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => setSwapTplOpen(true)}
          title="Cambiar plantilla — solo cambia la apariencia, conserva el contenido"
        >
          <Icon name="grid" size={12} /> Plantilla
        </button>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => setShowCalendar(true)}
          title="Ver el plan en calendario — cómo cae la rutina en las fechas reales"
        >
          <span aria-hidden style={{ fontSize: 13 }}>📅</span> Calendario
        </button>
        {/* Regenerar contenido del plan con IA. SOLO disponible si el plan
            ya está vinculado a un cliente — la IA usa edad/peso/objetivo/
            lesiones del cliente como contexto. */}
        <button
          className="btn btn-ghost btn-sm al-pb-ai-btn"
          onClick={() => setAiGenOpen("all")}
          disabled={!linkedClientId}
          title={
            linkedClientId
              ? "Generar rutina, dieta y cardio con Claude — pisa el contenido actual"
              : "Vinculá un cliente al plan primero (botón cliente arriba)"
          }
        >
          <Icon name="sparkle" size={12} /> IA
        </button>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => setShowDrawer((v) => !v)}
          title="Detalles del plan, exportar PDF, enlace público"
        >
          <Icon name="settings" size={12} /> Detalles
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => save()}
          disabled={saving || !dirty}
        >
          Guardar
        </button>
        <button
          className="btn btn-primary btn-sm"
          onClick={handleShare}
          disabled={sharing}
        >
          <Icon name="send" size={12} /> Compartir
        </button>
      </div>

      {/* Layout 2 columnas — el panel de detalles vive como drawer abrible. */}
      <div className="al-pb-grid" style={{ gridTemplateColumns: "240px 1fr" }}>
        {/* === Sidebar de secciones === */}
        <aside className="al-pb-sidebar">
          <div className="al-pb-side-eyebrow">SECCIONES</div>
          <div className="al-pb-section-list">
            {plan.sections.map((s) => {
              const def = {
                label: SECTION_LABELS[s.type],
                desc: SECTION_DESC[s.type],
                icon: SECTION_ICONS[s.type],
                required: SECTION_REQUIRED[s.type],
              };
              return (
                <div
                  key={s.id}
                  className={
                    "al-pb-section " +
                    (s.enabled ? "" : "is-off ") +
                    (dragOverId === s.id ? "is-dragover" : "")
                  }
                  draggable
                  onDragStart={(e) => {
                    dragSrcId.current = s.id;
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (dragSrcId.current && dragSrcId.current !== s.id) {
                      setDragOverId(s.id);
                      e.dataTransfer.dropEffect = "move";
                    }
                  }}
                  onDragLeave={() => {
                    if (dragOverId === s.id) setDragOverId(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = dragSrcId.current;
                    setDragOverId(null);
                    dragSrcId.current = null;
                    if (src) reorderSections(src, s.id);
                  }}
                  onClick={() => {
                    // Scroll suave al preview de esta sección
                    const el = document.getElementById(`pg-${s.id}`);
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  <button
                    className={"al-pb-toggle " + (s.enabled ? "on" : "off")}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSection(s.id);
                    }}
                    title={s.enabled ? "Desactivar" : "Activar"}
                  >
                    <span className="al-pb-toggle-dot" />
                  </button>
                  <span className="al-pb-section-ic">
                    <Icon name={def.icon} size={16} />
                  </span>
                  <div className="al-pb-section-info">
                    <div className="al-pb-section-lbl">{def.label}</div>
                    <div className="al-pb-section-meta">{def.desc}</div>
                  </div>
                  {!def.required && (
                    <button
                      className="al-pb-section-del"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSection(s.id);
                      }}
                      title="Eliminar"
                    >
                      <Icon name="x" size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {available.length > 0 && (
            <div className="al-pb-side-add">
              <button
                className="al-pb-add-btn"
                onClick={() => setShowAddMenu((v) => !v)}
              >
                <Icon name="plus" size={12} /> Agregar sección
              </button>
              {showAddMenu && (
                <div className="al-pb-add-menu">
                  {available.map((t) => (
                    <button
                      key={t}
                      className="al-pb-add-item"
                      onClick={() => addSection(t)}
                    >
                      <Icon name={SECTION_ICONS[t]} size={14} />
                      <div>
                        <b>{SECTION_LABELS[t]}</b>
                        <i>{SECTION_DESC[t]}</i>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </aside>

        {/* === Preview central === */}
        <main
          className={
            "al-pb-preview" +
            (isDarkPlan ? " al-plan-dark" : "") +
            (densityClass ? " " + densityClass : "")
          }
          style={previewStyle}
        >
          {activeSections.length === 0 ? (
            <div className="al-pb-empty">
              <Icon name="grid" size={36} />
              <h3>No hay secciones activas</h3>
              <p>Activa una sección desde el panel izquierdo para empezar.</p>
            </div>
          ) : (
            <div className="al-pb-pages">
              {activeSections.map((s, i) => (
                <div className="al-pb-page-wrap" key={s.id} id={`pg-${s.id}`}>
                  <div className="al-pb-page-meta">
                    <span>{SECTION_LABELS[s.type]}</span>
                    <span>·</span>
                    <span>Página {i + 1} de {activeSections.length}</span>
                  </div>
                  <SectionView
                    section={s}
                    brand={plan.brand}
                    clientId={linkedClientId}
                    onChange={(next) => updateSection(s.id, next)}
                    onGenerateWithAi={(kind) => {
                      // Validamos cliente acá para mostrar un toast claro —
                      // sino el modal abriría con paywall raro o lista de
                      // clientes que no querés en este contexto.
                      if (!linkedClientId) {
                        toast("Vinculá un cliente al plan primero (botón cliente arriba)");
                        return;
                      }
                      setAiGenOpen(kind);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </main>

      </div>

      {swapTplOpen && (
        <SwapTemplateModal
          plan={plan}
          onClose={() => setSwapTplOpen(false)}
          onApply={(nextPlan) => {
            updatePlan(nextPlan);
            setSwapTplOpen(false);
            toast("Plantilla cambiada — el contenido se mantiene intacto");
          }}
        />
      )}

      {showCalendar && (
        <div className="al-modal-backdrop" onClick={() => setShowCalendar(false)}>
          <div
            className="al-modal al-cal-modal"
            role="dialog"
            aria-label="Calendario del plan"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="al-modal-hd">
              <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
                📅 Calendario del plan
              </h3>
              <button className="icon-btn" onClick={() => setShowCalendar(false)} title="Cerrar">
                <Icon name="x" size={16} />
              </button>
            </div>
            <div className="al-cal-modal-body">
              {(() => {
                const clientSec = plan.sections.find((s) => s.type === "client");
                const routineSec = plan.sections.find((s) => s.type === "routine");
                const startDate = (clientSec?.data as any)?.startDate || "";
                const endDate = (clientSec?.data as any)?.endDate || "";
                const weeks = ((routineSec?.data as any)?.weeks || []) as RoutineWeek[];
                return <PlanCalendar startDate={startDate} endDate={endDate} weeks={weeks} />;
              })()}
            </div>
          </div>
        </div>
      )}

      {aiGenOpen !== false && linkedClientId && (
        <AiGeneratePlanModal
          // Pasamos el cliente como locked: el modal lo hidrata internamente
          // con getClient() si detecta que vino como shim.
          lockedClient={{ id: linkedClientId, full_name: linkedClientName } as any}
          planId={dbId}
          // Pre-tildar el toggle correcto según de dónde vino el coach
          initialIncludeTraining={aiGenOpen === "all" || aiGenOpen === "training"}
          initialIncludeNutrition={aiGenOpen === "all" || aiGenOpen === "nutrition"}
          onClose={() => setAiGenOpen(false)}
          onApply={async (planDoc) => {
            // El coach aceptó. El mapper preserva las secciones que no fueron
            // generadas (rutina si solo pidió dieta, etc.).
            const nextSections = aiPlanDocToSections(plan.sections, planDoc);
            const nextPlan = { ...plan, sections: nextSections };
            // La hoja de medidas viene por defecto (solo si quedó vacía, para no
            // pisar ediciones). La calculadora de dieta se siembra si está en
            // blanco (weightKg 0) — para que no aparezca vacía tras generar.
            const measSection = nextSections.find((s) => s.type === "measurements");
            const measEmpty = !measSection || !((measSection.data as any)?.rows?.length);
            const dietSection = nextSections.find((s) => s.type === "diet");
            const calcBlank = !((dietSection?.data as any)?.calculator?.weightKg);
            if (linkedClientId && (measEmpty || calcBlank)) {
              try {
                const [client, measurements] = await Promise.all([
                  getClient(linkedClientId),
                  listMeasurements(linkedClientId),
                ]);
                if (measEmpty) prefillMeasurementsSection(nextPlan, client, measurements);
                if (calcBlank) seedDietCalculatorFromClient(nextPlan, client, measurements);
              } catch {
                // best-effort — el coach puede completar a mano
              }
            }
            updatePlan(nextPlan);
            toast("Plan actualizado con la generación de IA — revisalo antes de compartir");
          }}
        />
      )}

      {/* === Picker de cliente (popover en topbar) === */}
      {showClientPicker && (
        <ClientPickerPopover
          currentClientId={linkedClientId}
          onPick={linkClient}
          onUnlink={unlinkClient}
          onClose={() => setShowClientPicker(false)}
          onCreateNew={() => {
            setShowClientPicker(false);
            setShowNewClientModal(true);
          }}
        />
      )}

      {showNewClientModal && (
        <ClientFormModal
          mode="create"
          onClose={() => setShowNewClientModal(false)}
          onSaved={(c) => {
            setShowNewClientModal(false);
            // Auto-vincular el recién creado al plan actual
            linkClient(c);
          }}
        />
      )}

      {/* === Drawer de detalles (overlay derecho, on-demand) === */}
      {showDrawer && (
        <>
          <div
            className="al-pb-drawer-backdrop"
            onClick={() => setShowDrawer(false)}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.32)", zIndex: 80,
              animation: "alFade .15s ease",
            }}
          />
          <aside
            className="al-pb-meta"
            style={{
              position: "fixed", top: 0, right: 0, bottom: 0, width: 360, maxWidth: "100vw",
              background: "var(--bg)", borderLeft: "1px solid var(--border)", zIndex: 81,
              padding: "20px 20px 24px", overflowY: "auto",
              boxShadow: "-12px 0 40px -8px rgba(0,0,0,.18)",
              animation: "alSlideInR .22s cubic-bezier(.2,.8,.2,1)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16 }}>Detalles del plan</div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setShowDrawer(false)}
                title="Cerrar"
              >
                <Icon name="x" size={14} />
              </button>
            </div>

            <div className="al-pb-meta-section">
              <div className="al-pb-side-eyebrow">PLAN</div>
              <div className="al-pb-meta-row">
                <span className="muted tiny">Cliente</span>
                <b>{readClientName(plan) || "—"}</b>
              </div>
              <div className="al-pb-meta-row">
                <span className="muted tiny">Plantilla</span>
                <span>{plan.template}</span>
              </div>
              <div className="al-pb-meta-row">
                <span className="muted tiny">Secciones</span>
                <span>{activeSections.length} de {plan.sections.length} activas</span>
              </div>
            </div>

            <div className="al-pb-meta-section">
              <div className="al-pb-side-eyebrow">COMPARTIR</div>
              {shareUrl ? (
                <>
                  <input
                    className="al-pb-meta-input"
                    value={shareUrl}
                    readOnly
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      navigator.clipboard.writeText(shareUrl).then(() => toast("Copiado"));
                    }}
                    style={{ width: "100%", marginTop: 8 }}
                  >
                    Copiar enlace
                  </button>
                </>
              ) : (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleShare}
                  disabled={sharing}
                  style={{ width: "100%" }}
                >
                  <Icon name="send" size={12} /> Generar enlace público
                </button>
              )}
            </div>

            <div className="al-pb-meta-section">
              <div className="al-pb-side-eyebrow">EXPORTAR PDF</div>
              <button
                className="btn btn-secondary btn-sm"
                onClick={handlePrint}
                disabled={printing}
                style={{ width: "100%" }}
                title="Descarga un PDF A4 limpio para enviar al cliente"
              >
                <Icon name="upload" size={12} />{" "}
                {printing ? "Generando PDF…" : "Descargar PDF"}
              </button>
              <div className="tiny muted" style={{ marginTop: 6, lineHeight: 1.4 }}>
                {printing
                  ? "Generando el PDF — puede tardar unos segundos…"
                  : "Descarga un PDF A4 con la versión limpia para el cliente (sin controles de edición), con la marca de agua centrada en cada hoja."}
              </div>
            </div>

            <div className="al-pb-meta-section">
              <div className="al-pb-side-eyebrow">GUARDADO</div>
              <div className="tiny muted">
                {saving
                  ? "Guardando…"
                  : lastSavedAt
                  ? `Último: ${formatTime(lastSavedAt)}`
                  : dirty
                  ? "Cambios sin guardar"
                  : "Sincronizado"}
              </div>
            </div>
          </aside>
        </>
      )}

      {/* Toolbar contextual estilo Canva: aparece sobre cualquier campo enfocado */}
      <TextStylePopover containerSelector=".al-pb-preview" />

      {/* Versión imprimible (solo-lectura) — se monta solo al imprimir, en un
          portal a <body>. La maneja @media print. */}
      {printing && <PrintablePlanView plan={plan} />}
    </div>
    </EditorStyleProvider>
  );
}

// ----- Client chip + picker popover (topbar) -----

interface ClientChipProps {
  name: string;
  onClick: () => void;
}

function ClientChip({ name, onClick }: ClientChipProps) {
  const has = !!name;
  return (
    <button
      type="button"
      className={"al-pb-client-chip " + (has ? "has" : "empty")}
      onClick={onClick}
      title={has ? `Cliente: ${name} — clic para cambiar` : "Vincular cliente al plan"}
    >
      <Icon name="user" size={12} />
      <span>{has ? name : "Sin cliente"}</span>
    </button>
  );
}

interface ClientPickerPopoverProps {
  currentClientId: number | null;
  onPick: (c: ClientFull) => void;
  onUnlink: () => void;
  onClose: () => void;
  onCreateNew: () => void;
}

function ClientPickerPopover({ currentClientId, onPick, onUnlink, onClose, onCreateNew }: ClientPickerPopoverProps) {
  const [q, setQ] = useState("");
  const [clients, setClients] = useState<ClientFull[] | null>(null);
  const [loading, setLoading] = useState(true);

  // Carga inicial de clientes activos. Re-carga al cambiar `q` (debounce 200ms).
  useEffect(() => {
    let cancel = false;
    const t = window.setTimeout(async () => {
      setLoading(true);
      try {
        const list = await listClients({ status: "active", q: q || undefined });
        if (!cancel) setClients(list);
      } catch {
        if (!cancel) setClients([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    }, q ? 200 : 0);
    return () => {
      cancel = true;
      window.clearTimeout(t);
    };
  }, [q]);

  return (
    <>
      <div className="al-pb-popover-backdrop" onClick={onClose} />
      <div className="al-pb-client-popover" role="dialog" aria-label="Seleccionar cliente">
        <div className="al-pb-pop-head">
          <input
            className="al-pb-pop-search"
            placeholder="Buscar clientes…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
        </div>
        <div className="al-pb-pop-list">
          {loading ? (
            <div className="muted tiny" style={{ padding: 12 }}>Cargando…</div>
          ) : clients && clients.length > 0 ? (
            clients.map((c) => (
              <button
                key={c.id}
                className={"al-pb-pop-item " + (c.id === currentClientId ? "is-current" : "")}
                onClick={() => onPick(c)}
              >
                <span className="al-pb-pop-avatar" aria-hidden>
                  {c.full_name.slice(0, 1).toUpperCase()}
                </span>
                <span className="al-pb-pop-name">{c.full_name}</span>
                {c.id === currentClientId && (
                  <span className="al-pb-pop-current">Actual</span>
                )}
              </button>
            ))
          ) : (
            <div className="muted tiny" style={{ padding: 12 }}>
              {q ? "Sin resultados" : "Aún no tienes clientes"}
            </div>
          )}
        </div>
        <div className="al-pb-pop-foot">
          <button className="btn btn-ghost btn-sm" onClick={onCreateNew}>
            <Icon name="plus" size={12} /> Nuevo cliente
          </button>
          {currentClientId != null && (
            <button className="btn btn-ghost btn-sm" onClick={onUnlink}>
              Quitar cliente
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ----- Helpers -----

function readClientName(plan: PlanDoc): string {
  const cover = plan.sections.find((s) => s.type === "cover");
  if (cover) return (cover.data as any).clientName || "";
  const client = plan.sections.find((s) => s.type === "client");
  if (client) return (client.data as any).fullName || "";
  return "";
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" });
}

/**
 * Convierte un `Plan` del backend en un `PlanDoc` editable.
 *
 * Hoy el backend guarda `sections[]` directamente (ver `aetherlite_plans.py`).
 * Para retrocompatibilidad con planes muy viejos creados con el shape anterior
 * `data: { sections, brand, ... }`, también detectamos ese caso si viene
 * envuelto dentro de un único item `{ __wrapper: true, ... }`. El nuevo flujo
 * NO lo produce, pero conviene ser defensivo durante la migración.
 */
function adoptRemotePlan(remote: Plan): PlanDoc {
  const sections = Array.isArray(remote.sections) ? remote.sections : [];

  // Detectar legacy wrapper { __wrapper: true, brand, template, sections } — cero
  // planes en producción tienen este shape, pero protege ante backups antiguos.
  if (sections.length === 1 && sections[0]?.__wrapper && Array.isArray(sections[0].sections)) {
    const w: any = sections[0];
    return {
      id: w.id || `pl_${remote.id}`,
      name: remote.name,
      template: w.template || remote.template || "t-bold",
      brand: w.brand || null,
      sections: w.sections,
      createdAt: Date.parse(remote.created_at) || Date.now(),
      updatedAt: Date.parse(remote.updated_at) || Date.now(),
    };
  }

  if (sections.length > 0) {
    return {
      id: `pl_${remote.id}`,
      name: remote.name,
      template: remote.template || "t-bold",
      // brand persistido (Feature 3). Si el plan no tiene (planes legacy), queda
      // null y se hidrata desde brandFromUser + applyTemplatePaletteIfMissing.
      brand: remote.brand ?? null,
      sections,
      createdAt: Date.parse(remote.created_at) || Date.now(),
      updatedAt: Date.parse(remote.updated_at) || Date.now(),
    };
  }

  // Fallback: plan vacío con name del backend
  return defaultPlan({ name: remote.name, clientName: remote.client_name || "Cliente" });
}

/**
 * Si el plan tiene `template` asociado a una plantilla del catálogo y el coach
 * no tiene primary/accent en su brand, aplica la paleta del template al plan.
 * No pisa colores explícitos del coach (esos ganan siempre).
 *
 * Mutación in-place sobre `plan.brand` — devuelve el mismo objeto.
 */
function applyTemplatePaletteIfMissing(plan: PlanDoc): PlanDoc {
  const tpl = getTemplate(plan.template);
  if (!tpl) return plan;
  const brand = plan.brand ?? {};
  const next = {
    ...brand,
    primary: brand.primary || tpl.palette.primary,
    accent: brand.accent || tpl.palette.accent,
  };
  plan.brand = next;
  return plan;
}

/**
 * Mutaciones in-place para sincronizar el nombre del cliente con las secciones
 * `cover` y `client` del PlanDoc. UX: el preview se actualiza en vivo cuando el
 * coach selecciona un cliente desde el chip.
 */
function applyClientNameToSections(plan: PlanDoc, name: string): void {
  for (const s of plan.sections) {
    if (s.type === "cover") (s.data as any).clientName = name;
    if (s.type === "client") (s.data as any).fullName = name;
  }
}

// `prefillSectionsFromClient` y `prefillMeasurementsSection` viven ahora en
// `./plan/prefillFromClient` — se comparten con TemplatesScreen (al "Usar
// plantilla" con un cliente seleccionado el plan arranca precargado).
